<script lang="ts">
  import { connectPlayer, connectionStatus, disconnect, lastError } from '../lib/connection';
  import { view } from '../lib/view';
  import { playerId, switchHref } from '../lib/session';
  import { forgetRoom, loadPlayerForRejoin, savePlayer, touchPlayer } from '../lib/playerStore';
  import { onMount } from 'svelte';
  import { holdWakeLock } from '../lib/wakeLock';
  import Join from '../phone/Join.svelte';
  import Lobby from '../phone/Lobby.svelte';
  import Game from '../phone/Game.svelte';

  let { code = '' }: { code?: string } = $props();

  // A stale room (not seen for 3 h, or a finale over 10 min old) is forgotten here.
  const saved = loadPlayerForRejoin();
  // svelte-ignore state_referenced_locally
  const rejoin = saved?.code && (!code || code.toUpperCase() === saved.code) ? saved : null;
  if (rejoin) connectPlayer(rejoin.code, playerId(), rejoin.name, rejoin.emoji);

  // svelte-ignore state_referenced_locally
  let joinCode = $state(code);
  /** In (or rejoining) a room, so ROOM_NOT_FOUND means it ended rather than a bad code. */
  let wasIn = $state(rejoin !== null);

  const FORGET = new Set(['KICKED', 'ROOM_NOT_FOUND', 'ROOM_FULL', 'NAME_TAKEN', 'BAD_NAME']);
  $effect(() => { if ($lastError && FORGET.has($lastError.code)) forgetRoom(); });

  const ended = $derived(wasIn && $lastError?.code === 'ROOM_NOT_FOUND');

  function join(c: string, name: string, emoji: string) {
    wasIn = false;
    savePlayer({ code: c, name, emoji });
    connectPlayer(c, playerId(), name, emoji);
  }

  /** Leave the room for good: forget it, hang up and show the Join screen. */
  function leave() {
    forgetRoom();
    disconnect();
    view.set(null);
    lastError.set(null);
    wasIn = false;
    joinCode = '';
    if (/^\/[A-Za-z]{4}$/.test(location.pathname)) history.replaceState(null, '', '/' + location.search);
  }

  // Keep the phone awake for the whole game.
  onMount(holdWakeLock);

  const busy = $derived($connectionStatus === 'connecting' || $connectionStatus === 'reconnecting');
  const pv = $derived($view && $view.role === 'player' ? $view : null);
  $effect(() => {
    if (!pv) return;
    wasIn = true;
    touchPlayer(pv.code, pv.phase);
  });
</script>

{#if pv && pv.phase === 'lobby'}
  <Lobby view={pv} />
{:else if pv}
  <Game view={pv} onleave={leave} />
{:else if rejoin && wasIn && busy && !$lastError}
  <main class="progress"><h1 class="wordmark">BANGER!</h1><p>Rejoining {rejoin.code}…</p></main>
{:else}
  <Join code={ended ? '' : joinCode} initialName={saved?.name ?? ''} initialEmoji={saved?.emoji ?? ''} {busy} note={ended ? 'That game has ended.' : ''} onjoin={join} />
  <p class="switch"><a href={switchHref('display')}>Host on this screen</a></p>
{/if}

{#if pv && $connectionStatus !== 'connected'}
  <p class="reconnecting" role="status">Reconnecting…</p>
{/if}

<style>
  .progress { min-height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; text-align: center; padding: 16px; }
  h1 { margin: 0; font-size: 2.6rem; }
  .switch { text-align: center; padding-bottom: 24px; margin: 0; }
  .reconnecting {
    position: fixed; top: max(10px, env(safe-area-inset-top)); left: 50%; transform: translateX(-50%); z-index: 80; margin: 0;
    padding: 8px 18px; max-width: calc(100vw - 24px); border-radius: 999px; font-weight: 800; white-space: nowrap; background: #0a0618;
    border: 2px solid var(--cyan); box-shadow: 0 0 14px rgba(125, 249, 255, 0.6); animation: breathe 1.6s ease-in-out infinite;
  }
  @keyframes breathe { 50% { opacity: 0.6; } }
  @media (prefers-reduced-motion: reduce) { .reconnecting { animation: none; } }
</style>
